"use client"

import React, { useEffect, useMemo, useState } from 'react'
import './App.css'
import { BrowserRouter, Routes, Route, Navigate, Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Home, Users, Calendar, Gift, MessageSquare, Bell, LifeBuoy, Settings, ShieldCheck, LogIn, UserPlus, Menu, MoreVertical, Search as SearchIcon, LogOut, UserRound, CreditCard, Wallet, TrendingUp, Shield, Activity, UsersRound, DollarSign } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from './components/ui/sheet.jsx'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu.jsx'
import { VisuallyHidden } from './components/ui/visually-hidden.jsx'
import { ThemeProvider, initializeTheme } from './contexts/ThemeContext'
import { ThemeToggle } from './components/ThemeToggle'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import TeamManagement from './components/team/TeamManagement'
import InvitationAcceptance from './components/team/InvitationAcceptance'
import SettingsIntegrations from './components/admin/SettingsIntegrations'
import HomePage from './components/HomePage'
import CheckoutFlow from './components/CheckoutFlow'
import BillingSettings from './components/BillingSettings'
import WalletSettings from './components/WalletSettings'
import AdminWalletManagement from './components/admin/AdminWalletManagement'
import MonetizationStudio from './components/admin/MonetizationStudio'
import GrowthEngine from './components/admin/GrowthEngine'
import Security from './components/admin/Security'
import SystemHealth from './components/admin/SystemHealth'
import SupportHub from './components/admin/SupportHub'

// API base from env (DO NOT hardcode)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`
axios.defaults.withCredentials = true

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Simple session hook
function useSession() {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  useEffect(() => {
    let ignore = false
    async function fetchMe() {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true })
        if (!ignore) setUser(data.user)
      } catch (e) {
        if (!ignore) setUser(null)
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    fetchMe()
    return () => { ignore = true }
  }, [])
  return { loading, user, setUser }
}

const SessionContext = React.createContext(null)
function SessionProvider({ children }){
  const value = useSession()
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
function useSessionContext(){
  return React.useContext(SessionContext)
}

export { useSessionContext }

function MobileNavDrawer({ itemsMain, itemsAdmin }){
  const location = useLocation()
  const [open, setOpen] = useState(false)
  useEffect(()=>{ setOpen(false) }, [location.pathname])
  
  // Determine which navigation items to show based on current route
  const isAdminRoute = location.pathname.startsWith('/admin')
  const currentItems = isAdminRoute ? itemsAdmin : itemsMain
  const sectionTitle = isAdminRoute ? 'Admin' : 'Main'
  
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="icon-btn" aria-label="Open navigation menu" aria-expanded={open} aria-controls="mobile-nav">
          <Menu size={20} />
          <VisuallyHidden>
            {open ? 'Close navigation menu' : 'Open navigation menu to access all pages'}
          </VisuallyHidden>
        </button>
      </SheetTrigger>
      <SheetContent side="left" id="mobile-nav" className="p-2" aria-label="Mobile Navigation" aria-labelledby="mobile-nav-title" aria-describedby="mobile-nav-desc">
        <VisuallyHidden>
          <SheetTitle id="mobile-nav-title">Navigation Menu</SheetTitle>
          <SheetDescription id="mobile-nav-desc">
            {isAdminRoute ? 'Admin section navigation with management tools' : 'Main application navigation menu'}
          </SheetDescription>
        </VisuallyHidden>
        <nav className="drawer-list" role="navigation" aria-labelledby="mobile-nav-title">
          <div className="drawer-section-title" role="heading" aria-level="2">{sectionTitle}</div>
          {currentItems.map((it, i) => (
            <Link key={i} to={it.to} className="drawer-item" role="menuitem">
              <it.icon size={18} aria-hidden="true" />
              {it.label}
              <VisuallyHidden>
                Navigate to {it.label} section
              </VisuallyHidden>
            </Link>
          ))}
          <SheetClose asChild>
            <button className="btn secondary" style={{marginTop:12, width:'100%'}}>
              Close
              <VisuallyHidden>Close navigation menu</VisuallyHidden>
            </button>
          </SheetClose>
        </nav>
      </SheetContent>
    </Sheet>
  )
}

function Header() {
  const { user, setUser } = useSessionContext()
  const navigate = useNavigate()
  const itemsMain = [
    { to: '/app', label: 'Dashboard', icon: Home },
    { to: '/app/module-1', label: 'Module-1', icon: Users },
    { to: '/app/support', label: 'Support', icon: LifeBuoy },
    { to: '/app/settings', label: 'Settings', icon: Settings },
  ]
  const itemsAdmin = user?.effective_permissions?.includes('admin:access') ? [
    { to: '/admin', label: 'Dashboard', icon: Home },
    { to: '/admin/users', label: 'User Management', icon: UsersRound },
    { to: '/admin/team', label: 'Team Management', icon: Users },
    { to: '/admin/monetization', label: 'Monetization Studio', icon: DollarSign },
    { to: '/admin/ai', label: 'AI Management', icon: ShieldCheck },
    { to: '/admin/growth-engine', label: 'Growth Engine', icon: TrendingUp },
    { to: '/admin/security', label: 'Security', icon: Shield },
    { to: '/admin/system-health', label: 'System Health', icon: Activity },
    { to: '/admin/support', label: 'Support', icon: LifeBuoy },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ] : []

  const [showMobileSearch, setShowMobileSearch] = useState(false)

  async function handleLogout(){
    try{ await axios.post(`${API}/auth/logout`, {}, { withCredentials:true }) }catch(e){ /* ignore */ }
    setUser(null)
    navigate('/', { replace:true })
  }

  return (
    <header className="header">
      <div className="header-inner">
        {/* Left: Hamburger on mobile */}
        <div className="search-mobile-toggle">
          {user ? <MobileNavDrawer itemsMain={itemsMain} itemsAdmin={itemsAdmin} /> : null}
        </div>
        {/* Center: Brand */}
        <div className="brand" style={{marginInlineStart: 4}}>
          <span className="brand-badge" /> SaaS-Base
        </div>
        {/* Right: Actions */}
        <nav className="nav" aria-label="Header actions">
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Search (mobile: icon; desktop: input) */}
          <button className="icon-btn search-mobile-toggle" aria-label="Toggle search" onClick={()=>setShowMobileSearch(s=>!s)} aria-expanded={showMobileSearch}>
            <SearchIcon size={18} aria-hidden="true" />
            <VisuallyHidden>
              {showMobileSearch ? 'Hide search input' : 'Show search input to search the application'}
            </VisuallyHidden>
          </button>
          <div className="search-wrap search-desktop">
            <SearchIcon className="search-icon" size={16} aria-hidden="true" />
            <input aria-label="Search the application" className="search-input" placeholder="Search…" />
          </div>

          {!user && <Link to="/auth/login">Login</Link>}
          {!user && <Link to="/auth/signup">Sign up</Link>}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="icon-btn" aria-label="Account menu - Access profile and logout options">
                  <MoreVertical size={18} aria-hidden="true" />
                  <VisuallyHidden>
                    Open account menu for {user.name || user.email}
                  </VisuallyHidden>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/app">
                    <UserRound size={14} aria-hidden="true" /> My App
                    <VisuallyHidden>Navigate to main application</VisuallyHidden>
                  </Link>
                </DropdownMenuItem>
                {user?.effective_permissions?.includes('admin:access') && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin">
                      <ShieldCheck size={14} aria-hidden="true" /> Admin
                      <VisuallyHidden>Navigate to admin dashboard</VisuallyHidden>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e)=>{e.preventDefault(); handleLogout();}}>
                  <LogOut size={14} aria-hidden="true" /> Logout
                  <VisuallyHidden>Sign out of your account</VisuallyHidden>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>
      </div>
      {showMobileSearch && (
        <div className="search-mobile-bar">
          <div className="search-wrap" style={{width:'100%'}}>
            <SearchIcon className="search-icon" size={16} aria-hidden="true" />
            <input aria-label="Search the application" className="search-input" placeholder="Search…" style={{width:'100%'}} />
            <VisuallyHidden>
              Mobile search input - type to search across all sections
            </VisuallyHidden>
          </div>
        </div>
      )}
    </header>
  )
}

function MarketingHome() {
  return <HomePage />
}

// Auth Pages
function Login() {
  const { setUser } = useSessionContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  // Get redirect parameter from URL
  const searchParams = new URLSearchParams(location.search)
  const redirectUrl = searchParams.get('redirect')

  async function onSubmit(e){
    e.preventDefault()
    setError('')
    try{
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true })
      setUser(data.user)
      
      // Handle redirect parameter or use role-based default landing
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true })
      } else {
        navigate(data.user.role_key === 'super_admin' ? '/admin' : '/app', { replace: true })
      }
    }catch(err){
      setError('Invalid credentials')
    }
  }

  return (
    <div className="auth-wrap" role="main">
      <h2 style={{marginTop:0}}>Log in</h2>
      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div style={{marginTop:14, display:'flex', gap:8, flexWrap:'wrap'}}>
          <button className="btn" type="submit">
            <LogIn size={16} style={{marginRight:6}} aria-hidden="true" /> Sign in
            <VisuallyHidden>Sign in with your email and password</VisuallyHidden>
          </button>
          <Link className="btn secondary" to="/auth/signup">
            <UserPlus size={16} style={{marginRight:6}} aria-hidden="true" /> Create account
            <VisuallyHidden>Navigate to create new account page</VisuallyHidden>
          </Link>
        </div>
        <p className="helper" style={{marginTop:10}}>Test users: subscriber@test.com / Passw0rd! — admin@test.com / Passw0rd!</p>
      </form>
    </div>
  )
}

function Signup(){
  const { setUser } = useSessionContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  // Get redirect parameter from URL
  const searchParams = new URLSearchParams(location.search)
  const redirectUrl = searchParams.get('redirect')

  async function onSubmit(e){
    e.preventDefault()
    setError('')
    try{
      const { data } = await axios.post(`${API}/auth/signup`, { name, email, password }, { withCredentials: true })
      setUser(data.user)
      
      // Handle redirect parameter or use default landing
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true })
      } else {
        navigate('/app', { replace: true })
      }
    }catch(err){
      setError('Could not create account')
    }
  }

  return (
    <div className="auth-wrap" role="main">
      <h2 style={{marginTop:0}}>Create your account</h2>
      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" className="input" value={name} onChange={e=>setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div style={{marginTop:14}}>
          <button className="btn" type="submit">
            <UserPlus size={16} style={{marginRight:6}} aria-hidden="true" /> Create account
            <VisuallyHidden>Create your new account with the provided information</VisuallyHidden>
          </button>
        </div>
      </form>
    </div>
  )
}

// App Shell
function AppShell(){
  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Primary navigation">
        <h3>Main</h3>
        <nav role="navigation">
          <Link className="side-link" to="/app">
            <Home size={18} aria-hidden="true" /> Dashboard
            <VisuallyHidden>View main dashboard with overview and recent activity</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/app/module-1">
            <Users size={18} aria-hidden="true" /> Module-1
            <VisuallyHidden>Access Module-1 features and functionality</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/app/support">
            <LifeBuoy size={18} aria-hidden="true" /> Support
            <VisuallyHidden>Get help and access support resources</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/app/settings">
            <Settings size={18} aria-hidden="true" /> Settings
            <VisuallyHidden>Configure your preferences and account settings</VisuallyHidden>
          </Link>
        </nav>
      </aside>
      <main className="content">
        <Outlet/>
      </main>
    </div>
  )
}

// Admin Shell
function AdminShell(){
  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Admin navigation">
        <h3>Admin</h3>
        <nav role="navigation">
          <Link className="side-link" to="/admin">
            <Home size={18} aria-hidden="true" /> Dashboard
            <VisuallyHidden>View admin dashboard with system overview and metrics</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/users">
            <UsersRound size={18} aria-hidden="true" /> User Management
            <VisuallyHidden>Manage user accounts, roles, and permissions</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/team">
            <Users size={18} aria-hidden="true" /> Team Management
            <VisuallyHidden>Manage team members, invitations, and organizational structure</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/monetization">
            <DollarSign size={18} aria-hidden="true" /> Monetization Studio
            <VisuallyHidden>Configure pricing, subscriptions, and revenue settings</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/ai">
            <ShieldCheck size={18} aria-hidden="true" /> AI Management
            <VisuallyHidden>Manage AI agents, models, and automation settings</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/growth-engine">
            <TrendingUp size={18} aria-hidden="true" /> Growth Engine
            <VisuallyHidden>Accelerate user acquisition, retention, and revenue growth</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/security">
            <Shield size={18} aria-hidden="true" /> Security
            <VisuallyHidden>Monitor security, threats, and compliance</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/system-health">
            <Activity size={18} aria-hidden="true" /> System Health
            <VisuallyHidden>View system metrics, performance, and uptime</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/support">
            <LifeBuoy size={18} aria-hidden="true" /> Support
            <VisuallyHidden>Handle support tickets and customer assistance</VisuallyHidden>
          </Link>
          <Link className="side-link" to="/admin/settings">
            <Settings size={18} aria-hidden="true" /> Settings
            <VisuallyHidden>Configure platform settings and integrations</VisuallyHidden>
          </Link>
        </nav>
      </aside>
      <main className="content">
        <Outlet/>
      </main>
    </div>
  )
}

// Pages (placeholders)
const PlaceholderCard = ({title, items=["Coming soon", "Scaffold ready", "Add feature next"]}) => (
  <div className="grid grid-3">
    {items.map((t,i) => (
      <div key={i} className="card">
        <h3 style={{marginTop:0}}>{title} • {i+1}</h3>
        <p className="helper">{t}</p>
      </div>
    ))}
  </div>
)

const Dashboard = () => (<>
  <h2 className="page-title">Dashboard</h2>
  <PlaceholderCard title="Briefing"/>
</>)
const Module1 = () => (<>
  <h2 className="page-title">Module-1</h2>
  <PlaceholderCard title="Module-1 Content"/>
</>)
const Support = () => (<>
  <h2 className="page-title">Help Center</h2>
  <PlaceholderCard title="Assistant"/>
</>)
const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('general');
  const location = useLocation();

  // Check for tab parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab && ['general', 'billing', 'wallet', 'notifications', 'privacy'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location]);

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'billing', label: 'Billing & Usage', icon: CreditCard },
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: ShieldCheck }
  ];

  return (
    <div className="space-y-6">
      <h2 className="page-title">Settings</h2>
      
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">General Preferences</h3>
              <PlaceholderCard title="Profile Settings" />
            </div>
          )}
          
          {activeTab === 'billing' && <BillingSettings />}
          
          {activeTab === 'wallet' && <WalletSettings />}
          
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notification Preferences</h3>
              <PlaceholderCard title="Notification Settings" />
            </div>
          )}
          
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Privacy Settings</h3>
              <PlaceholderCard title="Data & Privacy" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin pages
const AdminDashboard = () => (<>
  <h2 className="page-title">Admin Dashboard</h2>
  <PlaceholderCard title="Overview"/>
</>)
const AdminUsers = () => (<>
  <h2 className="page-title">User Management</h2>
  <PlaceholderCard title="Users"/>
</>)
const AdminTeam = () => (
  <TeamManagement />
)
const AdminMonetization = () => (
  <MonetizationStudio />
)
const AdminAI = () => (<>
  <h2 className="page-title">AI Management</h2>
  <PlaceholderCard title="Agents"/>
</>)
const AdminGrowthEngine = () => (
  <GrowthEngine />
)
const AdminSecurityPage = () => (
  <Security />
)
const AdminSystemHealth = () => (
  <SystemHealth />
)
const AdminSupport = () => (
  <SupportHub />
)
const AdminSettings = () => (
  <div>
    <h2 className="page-title">Platform Settings</h2>
    <div className="tabs">
      <div className="tab-nav">
        <button className="tab-btn active">Integrations</button>
        <button className="tab-btn">Security</button>
        <button className="tab-btn">General</button>
      </div>
      <div className="tab-content">
        <SettingsIntegrations />
      </div>
    </div>
  </div>
)

// Guards
function RequireAuth({ children }){
  const { loading, user } = useSessionContext()
  const location = useLocation()
  if (loading) return <div className="container"><div className="card">Loading…</div></div>
  if (!user) return <Navigate to="/auth/login" replace state={{ from: location }} />
  return children
}
function RequireAdmin({ children }){
  const { loading, user } = useSessionContext()
  const location = useLocation()
  if (loading) return <div className="container"><div className="card">Loading…</div></div>
  if (!user) return <Navigate to="/auth/login" replace state={{ from: location }} />
  if (!user.effective_permissions?.includes('admin:access')) return <Navigate to="/app" replace />
  return children
}

function TopLayout(){
  return (
    <div>
      <Header/>
      <Outlet/>
    </div>
  )
}

function RoutesView(){
  return (
    <BrowserRouter>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <Routes>
              <Route index element={<MarketingHome/>} />
              <Route element={<TopLayout/>}>
                <Route path="auth">
                  <Route path="login" element={<Login/>} />
                  <Route path="signup" element={<Signup/>} />
                </Route>
                <Route path="invite/:token" element={<InvitationAcceptance />} />
                <Route path="checkout" element={<CheckoutFlow />} />
                {/* Main App */}
                <Route path="app" element={<RequireAuth><AppShell/></RequireAuth>}>
                  <Route index element={<Dashboard/>} />
                  <Route path="module-1" element={<Module1/>} />
                  <Route path="support" element={<Support/>} />
                  <Route path="settings" element={<SettingsPage/>} />
                </Route>
                {/* Admin */}
                <Route path="admin" element={<RequireAdmin><AdminShell/></RequireAdmin>}>
                  <Route index element={<AdminDashboard/>} />
                  <Route path="users" element={<AdminUsers/>} />
                  <Route path="team" element={<AdminTeam/>} />
                  <Route path="monetization" element={<AdminMonetization/>} />
                  <Route path="ai" element={<AdminAI/>} />
                  <Route path="growth-engine" element={<AdminGrowthEngine/>} />
                  <Route path="security" element={<AdminSecurityPage/>} />
                  <Route path="system-health" element={<AdminSystemHealth/>} />
                  <Route path="support" element={<AdminSupport/>} />
                  <Route path="settings" element={<AdminSettings/>} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace/>} />
            </Routes>
          </SessionProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default function App(){
  // Initialize theme early to prevent FOUC
  useEffect(() => {
    initializeTheme()
  }, [])
  
  return <RoutesView/>
}